'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { Icon } from '@/components/ui/icon';
import { InformationCircleIcon } from '@/components/ui/icon/glyphs';
import { cn } from '@/lib/ui/utils';
export interface PortfolioPanelHeaderInfo {
  /** Localized aria-label for the info button (also used as the accessible name). */
  label: string;
  onClick: () => void;
  /** Mirrors the host dialog's open state for `aria-expanded`. */
  expanded?: boolean;
}
export interface PortfolioPanelHeaderProps {
  title: ReactNode;
  info?: PortfolioPanelHeaderInfo;
  /** Right-side slot — typically a `<TabsList>` or button. */
  action?: ReactNode;
  className?: string;
}

/**
 * Unified header row for every portfolio panel (Positions, Theses, Gifts,
 * Referrals, Recent Activity). Composed of:
 *   - a section title (left)
 *   - an optional info / how-it-works trigger next to the title
 *   - an optional right-side action (segmented tab control, etc.)
 *
 * Rendered as a sibling inside `<PanelContent>` rather than via `<PanelHeader>`
 * so the gap between header and body honours the consumer's `gap-*`.
 */
export function PortfolioPanelHeader(t0) {
  const $ = _c(13);
  const {
    title,
    info,
    action,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex shrink-0 items-center justify-between gap-2", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== title) {
    t2 = <span className="text-body-lg font-medium text-foreground-secondary">{title}</span>;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== info) {
    t3 = info ? <button type="button" aria-label={info.label} aria-haspopup="dialog" aria-expanded={info.expanded ?? false} onClick={info.onClick} className="text-foreground-tertiary transition-colors hover:text-foreground-secondary"><Icon icon={InformationCircleIcon} className="size-3" aria-hidden="true" /></button> : null;
    $[4] = info;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <span className="flex items-center gap-1.5">{t2}{t3}</span>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = action ?? null;
  let t6;
  if ($[9] !== t1 || $[10] !== t4 || $[11] !== t5) {
    t6 = <div className={t1}>{t4}{t5}</div>;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}